import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import {
  defaultLanguage,
  languageOptions,
  supportedLanguages,
  translations,
} from "../content/translations"

const STORAGE_KEY = "barashka-language"

const I18nContext = createContext(null)

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value)

const normalizeLanguage = (value) => {
  if (!value || typeof value !== "string") {
    return defaultLanguage
  }

  const normalized = value.toLowerCase().split("-")[0]
  return normalized
}

const getPathValue = (source, path) => {
  return path.split(".").reduce((result, segment) => {
    if (!isPlainObject(result) && !Array.isArray(result)) {
      return undefined
    }

    return result[segment]
  }, source)
}

const interpolate = (template, params) => {
  if (typeof template !== "string") {
    return template
  }

  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_full, token) => {
    const replacement = getPathValue(params, token)
    return replacement === undefined || replacement === null
      ? ""
      : String(replacement)
  })
}

const readPreferredLanguage = () => {
  if (typeof window === "undefined") {
    return defaultLanguage
  }

  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored) {
    return normalizeLanguage(stored)
  }

  const browserLanguages = navigator.languages?.length
    ? navigator.languages
    : [navigator.language]

  for (const lang of browserLanguages) {
    const normalized = normalizeLanguage(lang)
    if (supportedLanguages.includes(normalized)) {
      return normalized
    }
  }

  return defaultLanguage
}

export const isSupportedLanguage = (languageCode) =>
  supportedLanguages.includes(normalizeLanguage(languageCode))

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(defaultLanguage)

  useEffect(() => {
    const preferred = readPreferredLanguage()
    if (isSupportedLanguage(preferred)) {
      setLanguageState(preferred)
    }
  }, [])

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, language)
    }
  }, [language])

  const setLanguage = useCallback((languageCode) => {
    const normalized = normalizeLanguage(languageCode)
    if (!isSupportedLanguage(normalized)) {
      return
    }

    setLanguageState(normalized)
  }, [])

  const t = useCallback(
    (key, params = {}) => {
      const currentTranslation = getPathValue(translations[language], key)
      const fallbackTranslation = getPathValue(translations[defaultLanguage], key)

      const resolved =
        currentTranslation === undefined ? fallbackTranslation : currentTranslation

      if (resolved === undefined) {
        return key
      }

      if (typeof resolved === "string") {
        return interpolate(resolved, params)
      }

      return resolved
    },
    [language]
  )

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      languageOptions,
      supportedLanguages,
      defaultLanguage,
    }),
    [language, setLanguage, t]
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const context = useContext(I18nContext)

  if (!context) {
    throw new Error("useI18n must be used inside I18nProvider")
  }

  return context
}
